import {
  isFidodbVirtualFieldKey,
  resolveFidodbVirtualSourceTable,
} from './fidodbVirtualFields';

export type TableJoinEdge = { tableA: string; tableB: string };

export function parseTableFromFieldKey(fieldKey: string): string | null {
  const virtualTable = resolveFidodbVirtualSourceTable(fieldKey);
  if (virtualTable) return virtualTable;

  const [table, column, ...rest] = (fieldKey || '').split('.');
  if (rest.length || !table || !column) return null;
  // Prefisso profilo virtuale senza mapping esplicito: non trattarlo come tabella SQL.
  if (table.toLowerCase() === 'fidodb') return null;
  return table.toLowerCase();
}

function normalizeTableName(table: string): string {
  return (table || '').toLowerCase();
}

export function buildAdjacency(
  edges: TableJoinEdge[],
): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>();

  const link = (a: string, b: string) => {
    const left = normalizeTableName(a);
    const right = normalizeTableName(b);
    if (!left || !right || left === right) return;
    if (!adjacency.has(left)) adjacency.set(left, new Set());
    if (!adjacency.has(right)) adjacency.set(right, new Set());
    adjacency.get(left)!.add(right);
    adjacency.get(right)!.add(left);
  };

  for (const { tableA, tableB } of edges) {
    link(tableA, tableB);
  }

  return adjacency;
}

function hasPath(
  start: string,
  target: string,
  adjacency: Map<string, Set<string>>,
): boolean {
  if (start === target) return true;
  const queue = [start];
  const visited = new Set<string>([start]);

  while (queue.length) {
    const current = queue.shift()!;
    for (const neighbor of Array.from(adjacency.get(current) || [])) {
      if (neighbor === target) return true;
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  return false;
}

/** Tutte le tabelle devono essere raggiungibili da almeno una main table candidata. */
export function areTablesJoinCompatible(
  tables: string[],
  edges: TableJoinEdge[],
  candidateMainTables: string[],
): boolean {
  const uniqueTables = Array.from(new Set(tables.filter(Boolean).map(normalizeTableName)));
  if (uniqueTables.length <= 1) return true;
  if (!candidateMainTables.length) {
    return false;
  }

  const normalizedMainTables = candidateMainTables.map(normalizeTableName);

  if (!edges.length) {
    // Senza archi join noti, blocca combinazioni cross-table.
    return false;
  }

  const adjacency = buildAdjacency(edges);

  return normalizedMainTables.some((mainTable) =>
    uniqueTables.every(
      (table) => table === mainTable || hasPath(mainTable, table, adjacency),
    ),
  );
}

export function collectTablesFromFieldKeys(fieldKeys: string[]): string[] {
  return Array.from(
    new Set(
      fieldKeys
        .map(parseTableFromFieldKey)
        .filter((table): table is string => !!table),
    ),
  );
}

export function isDatabaseFieldJoinCompatible(
  fieldKey: string,
  selectedSplitKeys: string[],
  selectedMetricKeys: string[],
  edges: TableJoinEdge[],
  candidateMainTables: string[],
): boolean {
  // Metriche/split virtuali Fidodb sono sempre ammessi (join gestiti dal profilo backend).
  if (isFidodbVirtualFieldKey(fieldKey)) return true;

  const fieldTable = parseTableFromFieldKey(fieldKey);
  if (!fieldTable) return true;

  const otherSplitKeys = selectedSplitKeys.filter((key) => key !== fieldKey);
  const otherMetricKeys = selectedMetricKeys.filter((key) => key !== fieldKey);

  const tables = collectTablesFromFieldKeys([
    fieldKey,
    ...otherSplitKeys,
    ...otherMetricKeys,
  ]);

  return areTablesJoinCompatible(tables, edges, candidateMainTables);
}

/** Con selezione sull'altro lato, mostra solo campi join-compatibili. */
export function shouldShowDatabaseField(
  fieldKey: string,
  selectedSplitKeys: string[],
  selectedMetricKeys: string[],
  edges: TableJoinEdge[],
  candidateMainTables: string[],
  fieldKind: 'split' | 'metric',
): boolean {
  const oppositeSelected =
    fieldKind === 'split'
      ? selectedMetricKeys.length > 0
      : selectedSplitKeys.length > 0;

  if (!oppositeSelected) return true;

  return isDatabaseFieldJoinCompatible(
    fieldKey,
    selectedSplitKeys,
    selectedMetricKeys,
    edges,
    candidateMainTables,
  );
}
